import React, { useMemo, useState } from "react";
import { Box, ButtonBase, Grid, Paper, Typography } from "@mui/material";
import { DiaCalendario } from "./DiaCalendario";
import { TarjetaEvento } from "./TarjetaEvento";

const nombresDias = ["Dill", "Dima", "Dime", "Dij", "Div", "Dis", "Diu"];

const getCalendarDateId = (day, month, year) => (
	`${String(day).padStart(2, "0")}-${String(month + 1).padStart(2, "0")}-${year}`
);

const getMondayBasedWeekday = (date) => {
	const weekday = date.getDay();
	return weekday === 0 ? 6 : weekday - 1;
};

const buildMonthDays = (month, year) => {
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const firstWeekday = getMondayBasedWeekday(new Date(year, month, 1));
	const cells = [];

	for (let index = 0; index < firstWeekday; index++) {
		cells.push(null);
	}

	for (let day = 1; day <= daysInMonth; day++) {
		cells.push(day);
	}

	while (cells.length % 7 !== 0) {
		cells.push(null);
	}

	return cells;
};

const minutosDesdeHora = (hora) => {
	if (!hora) {
		return Number.MAX_SAFE_INTEGER;
	}

	const [hours = "99", minutes = "99"] = String(hora).slice(0, 5).split(":");
	return (Number(hours) * 60) + Number(minutes);
};

const ordenarEventosPorHora = (a, b) => {
	const diffHora = minutosDesdeHora(a.hora_inicio) - minutosDesdeHora(b.hora_inicio);

	if (diffHora !== 0) {
		return diffHora;
	}

	return Number(a.id || 0) - Number(b.id || 0);
};

export const CalendarioGrid = ({
	todayId,
	mesActual,
	anioActual,
	tareas,
	estados,
	opTrabajadores,
	opciones,
	abrirFormularioCrearEvento
}) => {
	const [diasExpandido, setDiasExpandido] = useState({});
	const [, setDialogOpen] = useState(false);

	const nombreMes = useMemo(() => (
		new Date(anioActual, mesActual, 1).toLocaleString("ca-ES", { month: "long" })
	), [anioActual, mesActual]);

	const diasMes = useMemo(() => (
		buildMonthDays(mesActual, anioActual)
	), [anioActual, mesActual]);

	const tareasPorDia = useMemo(() => {
		return tareas.reduce((acc, tarea) => {
			if (!acc[tarea.fecha]) {
				acc[tarea.fecha] = [];
			}

			acc[tarea.fecha].push(tarea);
			return acc;
		}, {});
	}, [tareas]);

	const toggleExpandirDia = (idDia) => {
		setDiasExpandido((prev) => ({
			...prev,
			[idDia]: !prev[idDia],
		}));
	};

	return (
		<>
			<div className="calendar-scroll">
					<Box className="calendar-month-block">
						<Box className="calendar-month-heading">
							<Typography variant="h6" className="calendar-month-title">
								{nombreMes} {anioActual}
							</Typography>
							<Typography className="calendar-month-range">
								{diasMes.filter(Boolean).length} dies
							</Typography>
						</Box>

						<Grid container spacing={1} className="calendar-weekdays" sx={{ display: {xs: 'none', md: 'flex'}}}>
							{nombresDias.map((dia) => (
								<Grid key={dia} size={{ xs: 0, sm: 1.714 }}>
									<Typography className="calendar-weekday">
										{dia}
									</Typography>
								</Grid>
							))}
						</Grid>

						<Grid container spacing={{ xs: 1.5, sm: 1 }}>
							{diasMes.map((dia, index) => {
								if (dia === null) {
									return (
										<Grid
											key={`empty-${index}`}
											className="calendar-empty-cell"
											size={{ xs: 0, sm: 1.714 }}
										/>
									);
								}

								const id = getCalendarDateId(dia, mesActual, anioActual);
								const tareasDelDia = tareasPorDia[id] || [];
								const tareasDelDiaOrdenadas = [...tareasDelDia].sort(ordenarEventosPorHora);
								const expandido = diasExpandido[id];
								const tareasVisibles = expandido
									? tareasDelDiaOrdenadas
									: tareasDelDiaOrdenadas.slice(0, 2);

								return (
									<Grid key={id} className="calendar-day-grid-item" size={{ xs: 12, sm: 1.714 }}>
										<Paper
											id={id}
											elevation={0}
											className={`calendar-day-paper ${id === todayId ? "calendar-day-paper-today" : ""}`}
										>
											<DiaCalendario
												id={id}
												dia={dia}
												esHoy={id === todayId}
												eventCount={tareasDelDiaOrdenadas.length}
												abrirFormularioCrearEvento={abrirFormularioCrearEvento}
												opciones={opciones}
											>
												{tareasVisibles.map((tarea) => (
													<TarjetaEvento
														key={tarea.id}
														id={tarea.id}
														tarea={tarea}
														estados={estados}
														setDialogOpen={setDialogOpen}
														opTrabajadores={opTrabajadores}
														opciones={opciones}
													/>
												))}
												{tareasDelDiaOrdenadas.length > 2 && (
													<ButtonBase
														onClick={() => toggleExpandirDia(id)}
														className="calendar-more"
													>
														{expandido
															? "- Veure menys"
															: `+ ${tareasDelDiaOrdenadas.length - 2} més`}
													</ButtonBase>
												)}
											</DiaCalendario>
										</Paper>
									</Grid>
								);
							})}
						</Grid>
					</Box>
			</div>
		</>
	);
};
